// src/components/common/Confirm/confirmStrategies.ts
import type { AppDispatch } from '@/store';
import { setErrorMessage } from '@/store/slices/ui/UISlice';
import { fetchOrgAdmins } from '@/store/thunks/organization/fetchOrgAdmins';
import { fetchOrgUsers } from '@/store/thunks/organization/fetchOrgUsers';
import { toggleAdminActiveThunk } from '@/store/thunks/administrators/toggleAdminActiveThunk';
import { forceLogoutAdminThunk } from '@/store/thunks/administrators/forceLogoutAdminThunk';
import { forceLoginResetAdminThunk } from '@/store/thunks/administrators/forceLoginResetAdminThunk';
import { deleteAdminThunk } from '@/store/thunks/administrators/deleteAdminThunk';
import { toggleUserActiveThunk } from '@/store/thunks/userForm/toggleUserActiveThunk';
import { forceLogoutUserThunk } from '@/store/thunks/userForm/forceLogoutUserThunk';
import { forceLoginResetUserThunk } from '@/store/thunks/userForm/forceLoginResetUserThunk';
import { deleteUserThunk } from '@/store/thunks/userForm/deleteUserThunk';
import { clearSessionToken } from '@/utils/sessionStorage';
import { logoutThunk } from '@/store/thunks/auth/logoutThunk';
import { OrgUserStatus } from '@/types/status.types';

type AdminKinds = 'toggleActive' | 'forceLogout' | 'forceLoginReset' | 'delete';
type UserKinds  = 'userToggleActive' | 'userForceLogout' | 'userForceLoginReset' | 'userDelete';

export type ConfirmContext =
  | { context: 'admins'; kind: AdminKinds; row: { id: string; name: string; status: 'Active'|'Inactive' } }
  | { context: 'users';  kind: UserKinds;  row: { id: string; name: string; status: OrgUserStatus } };

export interface ConfirmView {
  title: string;
  paragraphs: string[];
  onConfirm: () => Promise<void>;
}

interface Tools {
  dispatch: AppDispatch;
  close: () => void;
  /** id of the currently logged-in admin (or null if unknown) */
  currentAdminId?: string | null;
}

const withWrap = (
  work: () => Promise<unknown>,
  after: () => Promise<unknown>,
  tools: Tools
) => async() => {
  try {
    await work();
    await after();
    tools.close();
  } catch (err) {
    if (err instanceof Error && typeof err.message === 'string') {
      tools.dispatch(setErrorMessage(err.message));
    }
  }
};

export const buildConfirmView = (arg: ConfirmContext, tools: Tools): ConfirmView | null => {
  const { dispatch } = tools;

  if (arg.context === 'admins') {
    const { kind, row } = arg;

    switch (kind) {
      case 'toggleActive': {
        const goingTo = row.status === 'Active' ? 'Inactive' : 'Active';
        return {
          title: 'Confirm Admin Status Change',
          paragraphs: [
            `Please confirm that you wish to make ${row.name} ${goingTo}. ${row.name} will not be able to log into the Admin Center while inactive.`,
            `${row.name} will be logged out (if currently logged in) and will be advised that there is an issue with the account on the next login attempt.`,
          ],
          onConfirm: withWrap(
            async() => { await dispatch(toggleAdminActiveThunk({ id: row.id, currentStatus: row.status })).unwrap(); },
            async() => { await dispatch(fetchOrgAdmins()); },
            tools
          ),
        };
      }
      case 'forceLogout':
        return {
          title: 'Confirm Forced Logout',
          paragraphs: [
            `Please confirm that you wish to log ${row.name} out of the PortalSIX Admin Center.`,
            `${row.name} will be able to log back into the Admin Center using current credentials.`,
          ],
          onConfirm: withWrap(
            async() => { await dispatch(forceLogoutAdminThunk({ id: row.id })).unwrap(); },
            async() => { await dispatch(fetchOrgAdmins()); },
            tools
          ),
        };
      case 'forceLoginReset':
        return {
          title: 'Confirm Forced Credential Update',
          paragraphs: [
            `Please confirm that you wish to have ${row.name} update login credentials.`,
            `${row.name} will be logged out of the Admin Center. On next login, ${row.name} will be prompted to update the login credentials.`,
          ],
          onConfirm: withWrap(
            async() => {
              // 1) trigger server action
              await dispatch(forceLoginResetAdminThunk({ id: row.id })).unwrap();

              // 2) if this is the CURRENTLY LOGGED-IN admin, nuke session and log out
              if ((tools.currentAdminId !== null) && tools.currentAdminId === row.id) {
                clearSessionToken();
                await dispatch(logoutThunk());
              }
            },
            async() => { await dispatch(fetchOrgAdmins()); },
            tools
          ),
        };
      case 'delete':
        return {
          title: 'Delete Admin',
          paragraphs: [
            `Please confirm that you wish to delete ${row.name}.`,
            `This will permanently delete ${row.name}’s admin account. ${row.name} will be logged out of the Admin Center and will be unable to log in again.`,
          ],
          onConfirm: withWrap(
            async() => { await dispatch(deleteAdminThunk({ id: row.id })).unwrap(); },
            async() => { await dispatch(fetchOrgAdmins()); },
            tools
          ),
        };
    }
  }

  if (arg.context === 'users') {
    const { kind, row } = arg;

    switch (kind) {
      case 'userToggleActive': {
      const goingTo =
        row.status === OrgUserStatus.Active
          ? OrgUserStatus.Inactive
          : OrgUserStatus.Active;
        return {
          title: 'Confirm User Status Change',
          paragraphs: [
            `Please confirm that you wish to make ${row.name} ${goingTo}. ${row.name} will not be able to log into the Portal while inactive.`,
            `${row.name} will be logged out (if currently logged in) and will be advised that there is an issue with the account on the next login attempt.`,
          ],
          onConfirm: withWrap(
            async() => { await dispatch(toggleUserActiveThunk({ id: row.id, currentStatus: row.status })).unwrap(); },
            async() => { await dispatch(fetchOrgUsers()); },
            tools
          ),
        };
      }
      case 'userForceLogout':
        return {
          title: 'Confirm Forced Logout',
          paragraphs: [
            `Please confirm that you wish to log ${row.name} out of the Portal.`,
            `${row.name} will be able to log back in using current credentials.`,
          ],
          onConfirm: withWrap(
            async() => { await dispatch(forceLogoutUserThunk({ id: row.id })).unwrap(); },
            async() => { await dispatch(fetchOrgUsers()); },
            tools
          ),
        };
      case 'userForceLoginReset':
        return {
          title: 'Confirm Forced Credential Update',
          paragraphs: [
            `Please confirm that you wish to have ${row.name} update login credentials.`,
            `${row.name} will be logged out of the Portal. On next login, ${row.name} will be prompted to update the login credentials.`,
          ],
          onConfirm: withWrap(
            async() => { await dispatch(forceLoginResetUserThunk({ id: row.id })).unwrap(); },
            async() => { await dispatch(fetchOrgUsers()); },
            tools
          ),
        };
      case 'userDelete':
        return {
          title: 'Delete User',
          paragraphs: [
            `Please confirm that you wish to delete ${row.name}.`,
            `This will permanently delete ${row.name}’s account. ${row.name} will be logged out and will be unable to log in again.`,
          ],
          onConfirm: withWrap(
            async() => { await dispatch(deleteUserThunk({ id: row.id })).unwrap(); },
            async() => { await dispatch(fetchOrgUsers()); },
            tools
          ),
        };
    }
  }

  return null;
};
